import type { ContentItem } from '@sign/shared';
import { formatDateTime } from './datetime';

export interface ComposeSignature {
  /** Data URL (own fresh signature) or same-origin signed image URL. */
  src: string;
  signedAt: number;
  /** Signer name for the caption (multi-sign). */
  label?: string;
}

export interface ComposeSheetArgs {
  sheetName: string;
  content: ContentItem[];
  /** All collected signatures, signing order. Single-sign passes one. */
  signatures: ComposeSignature[];
  /** Completion time shown in the footer. */
  signedAt: number;
}

// Render at 2× for crisp on-screen display and clean print/archival output.
const SCALE = 2;
const W = 760; // logical page width (CSS px)
const PAD = 40;
const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('signature image load failed'));
    img.src = src;
  });
}

/**
 * Greedy wrap that handles CJK (no spaces) and over-long latin tokens by
 * breaking per character. Honors explicit "\n" as hard breaks.
 */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const lines: string[] = [];
  for (const para of String(text ?? '').split('\n')) {
    if (para === '') {
      lines.push('');
      continue;
    }
    let line = '';
    for (const ch of para) {
      const test = line + ch;
      if (line !== '' && ctx.measureText(test).width > maxW) {
        lines.push(line);
        line = ch;
      } else {
        line = test;
      }
    }
    lines.push(line);
  }
  return lines;
}

type Op =
  | { kind: 'text'; font: string; color: string; lines: string[]; lineH: number }
  | { kind: 'rule' }
  | { kind: 'gap'; h: number }
  | { kind: 'image'; img: HTMLImageElement; w: number; h: number };

/**
 * Compose the whole confirmation sheet — title + frozen content snapshot +
 * signature + signed timestamp — into a single white-background JPEG suitable
 * for archival, table write-back, and batch print. Replaces storing just the
 * bare signature strokes.
 */
export async function composeSheetImage(args: ComposeSheetArgs): Promise<string> {
  const { sheetName, content, signatures, signedAt } = args;
  const sigs = await Promise.all(
    signatures.map(async (s) => ({
      img: await loadImage(s.src),
      signedAt: s.signedAt,
      label: s.label,
    })),
  );
  if (sigs.length === 0) throw new Error('no signatures to compose');

  const innerW = W - PAD * 2;
  const meas = document.createElement('canvas').getContext('2d');
  if (!meas) throw new Error('canvas 2d context unavailable');

  const TITLE_F = `600 26px ${FONT_STACK}`;
  const LABEL_F = `600 15px ${FONT_STACK}`;
  const NAME_F = `600 16px ${FONT_STACK}`;
  const VALUE_F = `400 16px ${FONT_STACK}`;
  const FOOT_F = `400 14px ${FONT_STACK}`;

  const ops: Op[] = [];

  // Title
  meas.font = TITLE_F;
  ops.push({ kind: 'text', font: TITLE_F, color: '#1a1a1a', lines: wrapText(meas, sheetName, innerW), lineH: 36 });
  ops.push({ kind: 'gap', h: 8 });
  ops.push({ kind: 'rule' });
  ops.push({ kind: 'gap', h: 16 });

  // Content snapshot (empty for sign_only sheets)
  if (content.length > 0) {
    ops.push({ kind: 'text', font: LABEL_F, color: '#8a8a8a', lines: ['确认内容'], lineH: 22 });
    ops.push({ kind: 'gap', h: 10 });
    for (const item of content) {
      meas.font = NAME_F;
      ops.push({ kind: 'text', font: NAME_F, color: '#555555', lines: wrapText(meas, item.name, innerW), lineH: 22 });
      meas.font = VALUE_F;
      // 附件行的 value 就是文件名（现场照片-xx.jpg），下面马上会画出图片本身，
      // 再印一行文件名只是噪声，归档凭证上尤其多余。
      const hasImages = !!item.images?.length;
      if (!hasImages) {
        ops.push({ kind: 'text', font: VALUE_F, color: '#1a1a1a', lines: wrapText(meas, item.value || '—', innerW), lineH: 24 });
      }
      // Image attachments (same-origin signed URLs → canvas stays untainted).
      if (item.images) {
        for (const src of item.images) {
          try {
            const im = await loadImage(src);
            const r = Math.min(1, innerW / im.width, 360 / im.height);
            ops.push({ kind: 'gap', h: 6 });
            ops.push({ kind: 'image', img: im, w: Math.round(im.width * r), h: Math.round(im.height * r) });
          } catch {
            /* skip an image that fails to load; keep the rest of the sheet */
          }
        }
      }
      ops.push({ kind: 'gap', h: 12 });
    }
    ops.push({ kind: 'gap', h: 4 });
    ops.push({ kind: 'rule' });
    ops.push({ kind: 'gap', h: 16 });
  }

  // Signatures (one block per signer; captions only when there are several)
  ops.push({ kind: 'text', font: LABEL_F, color: '#8a8a8a', lines: ['签字'], lineH: 22 });
  ops.push({ kind: 'gap', h: 8 });
  const maxSigW = Math.min(320, innerW);
  const maxSigH = 160;
  sigs.forEach((s, i) => {
    const ratio = Math.min(maxSigW / s.img.width, maxSigH / s.img.height, 1);
    ops.push({
      kind: 'image',
      img: s.img,
      w: Math.round(s.img.width * ratio),
      h: Math.round(s.img.height * ratio),
    });
    if (sigs.length > 1) {
      ops.push({
        kind: 'text',
        font: FOOT_F,
        color: '#8a8a8a',
        lines: [`${s.label || `第 ${i + 1} 位签字人`} · ${formatDateTime(s.signedAt)}`],
        lineH: 20,
      });
    }
    ops.push({ kind: 'gap', h: 10 });
  });
  ops.push({ kind: 'gap', h: 4 });
  ops.push({ kind: 'rule' });
  ops.push({ kind: 'gap', h: 10 });
  ops.push({ kind: 'text', font: FOOT_F, color: '#8a8a8a', lines: [`签署时间：${formatDateTime(signedAt)}`], lineH: 20 });

  // Total height
  let h = PAD;
  for (const op of ops) {
    if (op.kind === 'text') h += op.lines.length * op.lineH;
    else if (op.kind === 'gap') h += op.h;
    else if (op.kind === 'rule') h += 1;
    else if (op.kind === 'image') h += op.h;
  }
  const totalH = h + PAD;

  // Render
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(W * SCALE);
  canvas.height = Math.round(totalH * SCALE);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.scale(SCALE, SCALE);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, totalH);
  ctx.textBaseline = 'top';

  let y = PAD;
  for (const op of ops) {
    if (op.kind === 'text') {
      ctx.font = op.font;
      ctx.fillStyle = op.color;
      for (const line of op.lines) {
        ctx.fillText(line, PAD, y);
        y += op.lineH;
      }
    } else if (op.kind === 'gap') {
      y += op.h;
    } else if (op.kind === 'rule') {
      ctx.strokeStyle = '#ececec';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PAD, y + 0.5);
      ctx.lineTo(W - PAD, y + 0.5);
      ctx.stroke();
      y += 1;
    } else if (op.kind === 'image') {
      ctx.drawImage(op.img, PAD, y, op.w, op.h);
      y += op.h;
    }
  }

  // JPEG (white bg, no transparency needed) — ~70% smaller than PNG for a
  // text+signature sheet, negligible quality loss for archival/print.
  return canvas.toDataURL('image/jpeg', 0.92);
}
